import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Combobox, ETypeOfNotification, InputDate, Modal, setNotification } from 'medere-ui-framework';

import { CashSummaryDTO, CashRegisterFinancialAccountDTO } from '../../dto/locationcash/LocationCashDTO';
import { MovementTypeOption, MovementResponseDTO, CreateMovementRequestDTO, FinancialAccountDTO, TransferRequestDTO } from '../../dto/FinancialAccountDTO';
import { getActiveSession, getCashSummary, getCashRegisterFinancialAccounts, releaseUser } from '../../services/LocationCashController';
import { createMovement, createTransfer, getMovementTypes, listMovements, getAllFinancialAccounts } from '../../services/FinancialAccountController';
import { getFieldErrorMessage, INSUFFICIENT_BALANCE, parseApiError } from '../../services/apiError';
import FieldError from '../../components/FieldError/FieldError';
import { useLocationDeskContext } from './context/LocationDeskContext';
import { formatDateTime, formatMoney, dateParam } from './utils';
import CashSummaryCards from './components/CashSummaryCards';
import CloseAndReleaseModal from './components/CloseAndReleaseModal';

const OUTGOING_TRANSFER_TYPE_ID = 1;
const ADJUSTMENT_TYPE_ID = 4;
const INCOMING_TRANSFER_TYPE_ID = 14;

const SESSION_PAGE_SIZE = 10;

export default function ActiveDeskView() {
    const dispatch = useDispatch();
    const { activeSession, activeLocationId, setCurrentView, setActiveSession, setActiveLocationId, navigateToMovements } = useLocationDeskContext();

    const [cashSummary, setCashSummary] = useState<CashSummaryDTO | null>(null);
    const [hasCashRegister, setHasCashRegister] = useState<boolean | null>(null);
    const [loadingCash, setLoadingCash] = useState(true);

    // Modal simple de liberación (sin caja)
    const [releaseModalVisible, setReleaseModalVisible] = useState(false);
    const [releasing, setReleasing] = useState(false);

    // Modal de cierre + firma + liberación
    const [closeAndReleaseVisible, setCloseAndReleaseVisible] = useState(false);

    // Modal nuevo movimiento (incluye transferencia como tipo)
    const [movModalVisible, setMovModalVisible] = useState(false);
    const [movementTypes, setMovementTypes] = useState<MovementTypeOption[]>([]);
    const [movAccounts, setMovAccounts] = useState<CashRegisterFinancialAccountDTO[]>([]);
    const [movSelectedAccount, setMovSelectedAccount] = useState<CashRegisterFinancialAccountDTO | null>(null);
    const [movType, setMovType] = useState<MovementTypeOption | null>(null);
    const [movAmount, setMovAmount] = useState<number | null>(null);
    const [movDate, setMovDate] = useState<Date>(new Date());
    const [movNotes, setMovNotes] = useState('');
    const [creatingMovement, setCreatingMovement] = useState(false);
    const [allFinancialAccounts, setAllFinancialAccounts] = useState<FinancialAccountDTO[]>([]);
    const [transferCounterpart, setTransferCounterpart] = useState<FinancialAccountDTO | null>(null);
    const [movAmountError, setMovAmountError] = useState<string | null>(null);
    const movAmountRef = useRef<HTMLInputElement>(null);

    const isOutgoingTransfer = movType?.id === OUTGOING_TRANSFER_TYPE_ID;
    const isIncomingTransfer = movType?.id === INCOMING_TRANSFER_TYPE_ID;
    const isTransfer = isOutgoingTransfer || isIncomingTransfer;

    // Grilla movimientos de sesión
    const [sessionMovements, setSessionMovements] = useState<MovementResponseDTO[]>([]);
    const [sessionTotal, setSessionTotal] = useState(0);
    const [sessionFirst, setSessionFirst] = useState(0);
    const [loadingSession, setLoadingSession] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);


    useEffect(() => {
        if (!activeLocationId) return;
        setLoadingCash(true);

        getActiveSession(activeLocationId)
            .then((session) => setActiveSession(session))
            .catch(console.error);

        getCashSummary(activeLocationId)
            .then((summary) => {
                setCashSummary(summary);
                setHasCashRegister(true);
            })
            .catch((err) => {
                if (err.response?.status === 404) {
                    setHasCashRegister(false);
                } else {
                    console.error(err);
                    setHasCashRegister(false);
                }
            })
            .finally(() => setLoadingCash(false));
    }, [activeLocationId]);

    // ── Movimientos de sesión ──
    const loadSessionPage = useCallback((offset: number) => {
        if (!cashSummary || !activeSession) return;
        setLoadingSession(true);
        listMovements({
            cashRegisterId: cashSummary.locationCashRegisterId,
            sessionId: activeSession.sessionId,
            offset,
            limit: SESSION_PAGE_SIZE,
        })
            .then(res => {
                setSessionMovements(res.items);
                setSessionTotal(res.totalCount);
            })
            .catch(console.error)
            .finally(() => setLoadingSession(false));
    }, [cashSummary, activeSession]);

    useEffect(() => {
        if (!cashSummary || !activeSession) return;
        setSessionFirst(0);
        loadSessionPage(0);
    }, [loadSessionPage, refreshKey]);

    const onSessionPage = (e: DataTablePageEvent) => {
        setSessionFirst(e.first);
        loadSessionPage(e.first);
    };

    const handleShowHistory = () => {
        if (activeLocationId) navigateToMovements(activeLocationId);
    };

    const goBackToSelection = () => {
        setActiveSession(null);
        setActiveLocationId(null);
        setCurrentView('selection');
    };

    // Liberación simple (sin caja)
    const handleSimpleRelease = async () => {
        if (!activeLocationId) return;
        setReleasing(true);
        try {
            await releaseUser(activeLocationId);
            dispatch(setNotification({
                title: 'Puesto liberado',
                desc: 'Se liberó el puesto de atención exitosamente',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            goBackToSelection();
        } catch (err) {
            console.error(err);
            dispatch(setNotification({
                title: 'Error',
                desc: 'No se pudo liberar el puesto de atención',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setReleasing(false);
            setReleaseModalVisible(false);
        }
    };

    const handleReleaseClick = () => {
        if (hasCashRegister && cashSummary) {
            // Con caja → cerrar, firmar y liberar
            setCloseAndReleaseVisible(true);
        } else {
            // Sin caja → confirmación simple
            setReleaseModalVisible(true);
        }
    };

    // ── Nuevo movimiento (incluye transferencia como tipo) ──

    const handleOpenMovModal = async () => {
        if (!cashSummary) return;
        setMovType(null);
        setMovAmount(null);
        setMovDate(new Date());
        setMovNotes('');
        setTransferCounterpart(null);
        setMovAmountError(null);
        setMovModalVisible(true);
        try {
            const [registerAccounts, financialAccounts, types] = await Promise.all([
                getCashRegisterFinancialAccounts(cashSummary.locationCashRegisterId),
                getAllFinancialAccounts(),
                getMovementTypes(),
            ]);
            const active = registerAccounts.filter(a => a.active);
            setMovAccounts(active);
            setMovSelectedAccount(active.find(a => a.isDefault) ?? active[0] ?? null);
            setAllFinancialAccounts(financialAccounts.filter((a: FinancialAccountDTO) => a.active));
            setMovementTypes(types);
        } catch (err) {
            console.error(err);
            setMovAccounts([]);
            setMovSelectedAccount(null);
            setAllFinancialAccounts([]);
            setMovementTypes([]);
        }
    };

    const handleMovTypeChange = (type: MovementTypeOption | null) => {
        setMovType(type);
        setMovAmount(null);
        setTransferCounterpart(null);
        setMovAmountError(null);
    };

    // Saldo insuficiente → marcador en Monto; cualquier otro error → card flotante
    const handleMovementError = (error: unknown, title: string) => {
        const apiError = parseApiError(error);
        const amountError = apiError.errorCode === INSUFFICIENT_BALANCE
            ? getFieldErrorMessage(apiError, 'amount')
            : undefined;
        if (amountError) {
            setMovAmountError(amountError);
            movAmountRef.current?.focus();
            return;
        }
        dispatch(setNotification({
            title,
            desc: apiError.message,
            icon: 'pi-times',
            typeOfNotification: ETypeOfNotification.ERROR,
        }));
    };

    const handleMovAmountChange = (value: number | null) => {
        setMovAmount(value);
        setMovAmountError(null);
    };

    const filteredCounterpartAccounts = allFinancialAccounts.filter(
        a => a.financialAccount !== movSelectedAccount?.financialAccountId,
    );

    const handleSubmitMovModal = async () => {
        if (!movSelectedAccount || !movType) return;

        if (isTransfer) {
            if (!transferCounterpart) return;
            if (movAmount == null || movAmount <= 0) {
                dispatch(setNotification({
                    title: 'Monto inválido',
                    desc: 'El monto debe ser mayor a 0',
                    icon: 'pi-times',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }));
                return;
            }
            setCreatingMovement(true);
            try {
                const body: TransferRequestDTO = isOutgoingTransfer
                    ? { origen: movSelectedAccount.financialAccountId, destino: transferCounterpart.financialAccount, amount: movAmount, notes: movNotes.trim() || undefined }
                    : { origen: transferCounterpart.financialAccount, destino: movSelectedAccount.financialAccountId, amount: movAmount, notes: movNotes.trim() || undefined };
                await createTransfer(body);
                dispatch(setNotification({
                    title: 'Transferencia realizada',
                    icon: 'pi-check-circle',
                    typeOfNotification: ETypeOfNotification.INFO,
                }));
                setMovModalVisible(false);
                setRefreshKey(v => v + 1);
                getCashSummary(activeLocationId!).then(setCashSummary).catch(console.error);
            } catch (error) {
                handleMovementError(error, 'No se pudo realizar la transferencia');
            } finally {
                setCreatingMovement(false);
            }
        } else {
            if (movAmount == null || (movType.id !== ADJUSTMENT_TYPE_ID && movAmount <= 0)) {
                dispatch(setNotification({
                    title: 'Monto inválido',
                    desc: movType.id === ADJUSTMENT_TYPE_ID ? 'Ingrese un monto (puede ser negativo para ajuste)' : 'El monto debe ser mayor a 0',
                    icon: 'pi-times',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }));
                return;
            }
            setCreatingMovement(true);
            try {
                const body: CreateMovementRequestDTO = {
                    financialAccountId: movSelectedAccount.financialAccountId,
                    movementTypeId: movType.id,
                    amount: movAmount,
                    dateTime: dateParam(movDate),
                    notes: movNotes.trim() || undefined,
                    sourceEntity: 'MANUAL',
                };
                await createMovement(body);
                dispatch(setNotification({
                    title: 'Movimiento registrado',
                    icon: 'pi-check-circle',
                    typeOfNotification: ETypeOfNotification.INFO,
                }));
                setMovModalVisible(false);
                setRefreshKey(v => v + 1);
                getCashSummary(activeLocationId!).then(setCashSummary).catch(console.error);
            } catch (error) {
                handleMovementError(error, 'No se pudo registrar el movimiento');
            } finally {
                setCreatingMovement(false);
            }
        }
    };

    const isMovSubmitDisabled = isTransfer
        ? !movSelectedAccount || !transferCounterpart || movAmount == null || movAmount <= 0
        : !movSelectedAccount || !movType || movAmount == null;

    if (!activeSession) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div>
            <div className="active-desk-header">
                <div className="flex align-items-center gap-3">
                    <Button
                        icon="pi pi-arrow-left"
                        className="buttonMain3"
                        tooltip="Volver a selección"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => setCurrentView('selection')}
                    />
                    <div>
                        <h1 className="active-desk-title">{activeSession.locationDescription}</h1>
                        <div className="active-desk-meta">
                            <span>
                                <i className="pi pi-user" />
                                {activeSession.username}
                            </span>
                            <span>
                                <i className="pi pi-clock" />
                                Inicio: {formatDateTime(activeSession.startDatetime)}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="active-desk-actions">
                    {hasCashRegister && (
                        <Button
                            label="Nuevo movimiento"
                            icon="pi pi-plus"
                            className="buttonMain"
                            onClick={handleOpenMovModal}
                        />
                    )}
                    <Button
                        label="Liberar puesto"
                        icon="pi pi-sign-out"
                        className="buttonMain2"
                        onClick={handleReleaseClick}
                    />
                </div>
            </div>

            {loadingCash ? (
                <div className="flex justify-content-center py-4">
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                </div>
            ) : hasCashRegister && cashSummary ? (
                <div>
                    {cashSummary.allowsSummary && (
                        <div className="mb-4">
                            <CashSummaryCards summary={cashSummary} />
                        </div>
                    )}

                    <div className="flex align-items-center justify-content-between mb-3">
                        <h3 style={{ margin: 0 }}>Movimientos de esta sesión</h3>
                        <Button
                            label="Ver historial completo"
                            icon="pi pi-history"
                            className="buttonMain3"
                            onClick={handleShowHistory}
                        />
                    </div>

                    <DataTable
                        value={sessionMovements}
                        size="small"
                        stripedRows
                        lazy
                        paginator
                        rows={SESSION_PAGE_SIZE}
                        first={sessionFirst}
                        totalRecords={sessionTotal}
                        onPage={onSessionPage}
                        loading={loadingSession}
                        emptyMessage="No hay movimientos registrados en esta sesión."
                    >
                        <Column field="dateTime" header="Fecha/Hora" body={(row: MovementResponseDTO) => formatDateTime(row.dateTime)} style={{ width: '160px' }} />
                        <Column field="username" header="Usuario" body={(row: MovementResponseDTO) => row.username ?? '—'} style={{ width: '120px' }} />
                        <Column field="financialAccountName" header="Cuenta" style={{ width: '150px' }} />
                        <Column field="movementTypeDescription" header="Tipo" style={{ width: '140px' }} />
                        <Column field="notes" header="Notas" body={(row: MovementResponseDTO) => row.notes ?? '—'} style={{ minWidth: '180px' }} />
                        <Column
                            field="amount"
                            header="Monto"
                            body={(row: MovementResponseDTO) => (
                                <span className="font-semibold" style={row.amount < 0 ? { color: '#DC3545' } : undefined}>
                                    {formatMoney(row.amount)}
                                </span>
                            )}
                            style={{ width: '130px', textAlign: 'right' }}
                        />
                    </DataTable>

                </div>
            ) : (
                <div className="active-desk-info-banner">
                    <i className="pi pi-info-circle" />
                    <span>Este puesto no tiene caja asociada.</span>
                </div>
            )}

            {/* Modal simple — puesto sin caja */}
            <Modal
                visible={releaseModalVisible}
                setVisible={setReleaseModalVisible}
                header="Liberar puesto"
                width="w-medium"
                closable={!releasing}
            >
                <div>
                    <p style={{ color: '#5A6676' }}>
                        ¿Confirmar la liberación del puesto <strong>{activeSession.locationDescription}</strong>?
                    </p>
                    <div className="flex flex-column align-items-center gap-2 mt-3">
                        <Button
                            label="Confirmar"
                            icon="pi pi-sign-out"
                            className="w-full buttonMain"
                            onClick={handleSimpleRelease}
                            loading={releasing}
                        />
                        <Button
                            label="Cancelar"
                            className="w-full buttonMain2"
                            onClick={() => setReleaseModalVisible(false)}
                            disabled={releasing}
                        />
                    </div>
                </div>
            </Modal>

            {/* Modal cierre + firma + liberación — puesto con caja */}
            {cashSummary && (
                <CloseAndReleaseModal
                    visible={closeAndReleaseVisible}
                    onHide={() => setCloseAndReleaseVisible(false)}
                    hospitalLocationId={activeLocationId!}
                    locationDescription={activeSession.locationDescription}
                    cashSummary={cashSummary}
                    onSuccess={goBackToSelection}
                />
            )}

            {/* Modal nuevo movimiento */}
            <Modal
                visible={movModalVisible}
                setVisible={setMovModalVisible}
                header="Nuevo movimiento"
                width="w-medium"
                closable={!creatingMovement}
            >
                <div className="flex flex-column gap-3">
                    <div>
                        <Combobox
                            label={isOutgoingTransfer ? 'Cuenta origen *' : isIncomingTransfer ? 'Cuenta destino *' : 'Cuenta financiera *'}
                            list={movAccounts}
                            value={movSelectedAccount}
                            setValue={(v: CashRegisterFinancialAccountDTO | null) => {
                                setMovSelectedAccount(v);
                                setMovAmountError(null);
                            }}
                            optionLabel="financialAccountName"
                            placeholder="Seleccionar cuenta..."
                        />
                    </div>
                    <div>
                        <Combobox
                            label="Tipo de movimiento *"
                            list={movementTypes}
                            value={movType}
                            setValue={handleMovTypeChange}
                            optionLabel="description"
                            placeholder="Seleccionar tipo..."
                        />
                    </div>

                    {isTransfer && (
                        <div>
                            <Combobox
                                label={isOutgoingTransfer ? 'Cuenta destino *' : 'Cuenta origen *'}
                                list={filteredCounterpartAccounts}
                                value={transferCounterpart}
                                setValue={(v: FinancialAccountDTO | null) => {
                                    setTransferCounterpart(v);
                                    setMovAmountError(null);
                                }}
                                optionLabel="name"
                                placeholder="Seleccionar cuenta..."
                            />
                        </div>
                    )}

                    {isTransfer ? (
                        <div className="flex flex-column gap-1">
                            <label htmlFor="desk-transfer-amount" className="font-medium" style={{ fontSize: '13px' }}>Monto *</label>
                            <InputNumber
                                inputId="desk-transfer-amount"
                                inputRef={movAmountRef}
                                value={movAmount}
                                onValueChange={e => handleMovAmountChange(e.value ?? null)}
                                mode="currency"
                                currency="ARS"
                                locale="es-AR"
                                min={0}
                                disabled={creatingMovement}
                                className={movAmountError ? 'field-error-target' : undefined}
                                aria-invalid={movAmountError ? true : undefined}
                                aria-describedby={movAmountError ? 'desk-movement-amount-error' : undefined}
                            />
                            <FieldError id="desk-movement-amount-error" message={movAmountError} />
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="flex flex-column gap-1">
                                <label htmlFor="desk-movement-amount" className="font-medium" style={{ fontSize: '13px' }}>Monto *</label>
                                <InputNumber
                                    inputId="desk-movement-amount"
                                    inputRef={movAmountRef}
                                    value={movAmount}
                                    onValueChange={e => handleMovAmountChange(e.value ?? null)}
                                    mode="currency"
                                    currency="ARS"
                                    locale="es-AR"
                                    min={movType?.id === ADJUSTMENT_TYPE_ID ? -999999999 : 0}
                                    disabled={creatingMovement || !movType}
                                    className={movAmountError ? 'field-error-target' : undefined}
                                    aria-invalid={movAmountError ? true : undefined}
                                    aria-describedby={movAmountError ? 'desk-movement-amount-error' : undefined}
                                />
                                <FieldError id="desk-movement-amount-error" message={movAmountError} />
                                {movType?.id === ADJUSTMENT_TYPE_ID && !movAmountError && (
                                    <span style={{ fontSize: '11px', color: '#5A6676', marginTop: '2px' }}>
                                        <i className="pi pi-info-circle" style={{ fontSize: '11px', marginRight: '4px' }} />
                                        Ajuste: permite monto negativo
                                    </span>
                                )}
                            </div>
                            <InputDate
                                label="Fecha"
                                value={movDate}
                                onChange={(e: any) => setMovDate(e.value ?? new Date())}
                            />
                        </div>
                    )}

                    <div className="flex flex-column gap-1">
                        <label className="font-medium" style={{ fontSize: '13px' }}>Notas</label>
                        <InputTextarea
                            value={movNotes}
                            onChange={e => setMovNotes(e.target.value)}
                            rows={2}
                            autoResize
                            placeholder="Opcional"
                            disabled={creatingMovement}
                        />
                    </div>
                    <div className="flex justify-content-end gap-2 mt-2">
                        <Button
                            label="Cancelar"
                            className="buttonMain3"
                            onClick={() => setMovModalVisible(false)}
                            disabled={creatingMovement}
                        />
                        <Button
                            label={isTransfer ? 'Realizar transferencia' : 'Registrar movimiento'}
                            icon="pi pi-check"
                            className="buttonMain"
                            onClick={handleSubmitMovModal}
                            loading={creatingMovement}
                            disabled={isMovSubmitDisabled}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
}
